<template>
  <div class="chat-sessions-rates">
    <DataTable v-if="rates" :value="rates" :paginator="rates.length > 15" :rows="15" class="rates-table mx-auto w-full max-w-[50rem]" :tableStyle="{ width: '100%' }">
      <Column class="rates-table__column" field="country" :header="$t('global.country')" />
      <Column class="rates-table__column" field="convSessionRate" :header="$t('components.bmDefaultRates.convSession')">
        <template #body="{ data }">
          {{ data.convSessionRate ? data.convSessionRate.toFixed(4) + ' €' : '-' }}
        </template>
      </Column>
      <Column class="rates-table__column" field="promoInternationalRate" :header="$t('components.bmDefaultRates.promoInternationalRate')">
        <template #body="{ data }">
           {{ data.promoInternationalRate ? data.promoInternationalRate.toFixed(4) + ' €' : '-' }}
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import DataTable from "primevue/datatable";
import Column from "primevue/column";
const { locale } = useI18n();

const localeToCountryMap: Record<string, string> = {
  'uk-ua': 'Ukraine',
  'bg': 'Bulgaria',
  'hr': 'Croatia',
  'cs': 'Czech Republic',
  'da': 'Denmark',
  'nl': 'Netherlands',
  'et': 'Estonia',
  'fi': 'Finland',
  'fr': 'France',
  'de': 'Germany',
  'el': 'Greece',
  'hu': 'Hungary',
  'ga': 'Ireland',
  'it': 'Italy',
  'lv': 'Latvia',
  'lt': 'Lithuania',
  'mt': 'Malta',
  'pl': 'Poland',
  'pt': 'Portugal',
  'ro': 'Romania',
  'sk': 'Slovakia',
  'sl': 'Slovenia',
  'es': 'Spain',
  'sv': 'Sweden',
  'tr': 'Turkey',
  'my': 'Myanmar',
  'fil': 'Philippines',
  'ro-md': 'Moldova',
  'dv': 'Maldives',
  'hy': 'Armenia',
  'sq': 'Albania',
  'bs': 'Bosnia and Herzegovina',
  'sr': 'Serbia',
  'ka': 'Georgia',
  'ne': 'Nepal',
  'vi': 'Vietnam',
  'ko': 'South Korea',
  'he': 'Israel'
};

const filterCountry = computed(() => {
  // English shows all countries
  if (locale.value === 'en-us') {
    return undefined;
  }
  // All other locales map to their specific country
  return localeToCountryMap[locale.value] || 'Other countries';
});

const mockRates = [
  { country: "Ukraine", otpRate: 0.0276, transactoinalRate: 0.0276, promoRate: 0.0276, convSessionRate: 0.0276, promoInternationalRate: 0.0564, mmf: 175 },
  { country: "Germany", otpRate: 0.023, transactoinalRate: 0.023, promoRate: 0.023, convSessionRate: 0.023, promoInternationalRate: 0.0247, mmf: 115 },
  { country: "Poland", otpRate: 0.015, transactoinalRate: 0.015, promoRate: 0.015, convSessionRate: 0.015, promoInternationalRate: 0.0506, mmf: 115 },
  { country: "Romania", otpRate: 0.0115, transactoinalRate: 0.0115, promoRate: 0.0115, convSessionRate: 0.0115, promoInternationalRate: 0.0184, mmf: 115 },
  { country: "Bulgaria", otpRate: 0.0345, transactoinalRate: 0.0345, promoRate: 0.0345, convSessionRate: 0.0345, promoInternationalRate: 0.0345, mmf: 175 },
  { country: "Hungary", otpRate: 0.0173, transactoinalRate: 0.0173, promoRate: 0.0173, convSessionRate: 0.0173, promoInternationalRate: 0.0173, mmf: 115 },
  { country: "Czech Republic", otpRate: 0.0265, transactoinalRate: 0.0265, promoRate: 0.0265, convSessionRate: 0.0265, promoInternationalRate: 0.0265, mmf: 115 },
  { country: "Slovakia", otpRate: 0.0265, transactoinalRate: 0.0265, promoRate: 0.0265, convSessionRate: 0.0265, promoInternationalRate: 0.0265, mmf: 115 },
  { country: "Croatia", otpRate: 0.015, transactoinalRate: 0.015, promoRate: 0.015, convSessionRate: 0.015, promoInternationalRate: 0.023, mmf: 115 },
  { country: "Slovenia", otpRate: 0.0046, transactoinalRate: 0.0046, promoRate: 0.0046, convSessionRate: 0.0046, promoInternationalRate: 0.0276, mmf: 115 },
  { country: "Serbia", otpRate: 0.0098, transactoinalRate: 0.0098, promoRate: 0.0098, convSessionRate: 0.0098, promoInternationalRate: 0.0345, mmf: 115 },
  { country: "Bosnia and Herzegovina", otpRate: 0.0115, transactoinalRate: 0.0115, promoRate: 0.0115, convSessionRate: 0.0115, promoInternationalRate: 0.0184, mmf: 115 },
  { country: "Montenegro", otpRate: 0.0104, transactoinalRate: 0.0104, promoRate: 0.0104, convSessionRate: 0.0104, promoInternationalRate: 0.0276, mmf: 115 },
  { country: "North Macedonia", otpRate: 0.0058, transactoinalRate: 0.0058, promoRate: 0.0058, convSessionRate: 0.0058, promoInternationalRate: 0.0058, mmf: 115 },
  { country: "Albania", otpRate: 0.0104, transactoinalRate: 0.0104, promoRate: 0.0104, convSessionRate: 0.0104, promoInternationalRate: 0.0575, mmf: 115 },
  { country: "Moldova", otpRate: 0.0127, transactoinalRate: 0.0127, promoRate: 0.0127, convSessionRate: 0.0127, promoInternationalRate: 0.015, mmf: 115 },
  { country: "Georgia", otpRate: 0.0092, transactoinalRate: 0.0092, promoRate: 0.0092, convSessionRate: 0.0092, promoInternationalRate: 0.0403, mmf: 115 },
  { country: "Armenia", otpRate: 0.0104, transactoinalRate: 0.0104, promoRate: 0.0104, convSessionRate: 0.0104, promoInternationalRate: 0.0575, mmf: 115 },
  { country: "Lithuania", otpRate: 0.0104, transactoinalRate: 0.0104, promoRate: 0.0104, convSessionRate: 0.0104, promoInternationalRate: 0.0115, mmf: 115 },
  { country: "Latvia", otpRate: 0.0104, transactoinalRate: 0.0104, promoRate: 0.0104, convSessionRate: 0.0104, promoInternationalRate: 0.0115, mmf: 115 },
  { country: "Estonia", otpRate: 0.0104, transactoinalRate: 0.0104, promoRate: 0.0104, convSessionRate: 0.0104, promoInternationalRate: 0.0115, mmf: 115 },
  { country: "Cyprus", otpRate: 0.0086, transactoinalRate: 0.0086, promoRate: 0.0086, convSessionRate: 0.0086, promoInternationalRate: 0.0086, mmf: 115 },
  { country: "Greece", otpRate: 0.0219, transactoinalRate: 0.0219, promoRate: 0.0219, convSessionRate: 0.0219, promoInternationalRate: 0.0219, mmf: 115 },
  { country: "Turkey", otpRate: 0.015, transactoinalRate: 0.015, promoRate: 0.015, convSessionRate: 0.015, promoInternationalRate: 0.0506, mmf: 115 },
  { country: "Other countries", otpRate: 0.015, transactoinalRate: 0.015, promoRate: 0.015, convSessionRate: 0.015, promoInternationalRate: 0.0506, mmf: 115 }
];

const { rates } = useRatesTable('/rate/default', mockRates, filterCountry);
</script>

<style lang="scss">
.chat-sessions-rates {
  .rates-table {
    width: 100%;

    & .p-datatable-thead {
      margin-bottom: 10px;
      text-align: right;
    }

    &__column {
      text-align: left;
    }

    &__column:last-child {
      text-align: right;

      & .p-column-header-content {
        display: flex;
        justify-content: flex-end;
      }
    }
    
    // Middle column center logic
    &__column:nth-child(2) {
      text-align: center;
      & .p-column-header-content {
        display: flex;
        justify-content: center;
      }
    }

    & .p-datatable-tbody {
      padding-top: 10px;
    }
    
    // Add distinct styling to row borders like BMDefaultRates
    & .p-datatable-tbody > tr {
        border-bottom: 1px solid #f3f4f6;
    }
  }
}
</style>
